import * as React from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Bell, Ellipsis } from 'lucide-react';

import { LAYERS } from 'components/constants/layers';
import { MOBILE_TABBAR_HEIGHT } from 'components/constants/breakpoints';
import { T } from 'services/ThemeTokens';

import { IMobileTab, isPathActive } from './mobileNav';

export interface IMobileTabBar {
  tabs: IMobileTab[];
  pathname: string;
  /** Число на вкладке (непрочитанное, просроченное); 0 — без значка. */
  countOf: (to: string) => number;
  notificationsOpen: boolean;
  /** Непрочитанные уведомления — то же число, что у колокольчика шапки. */
  notificationsCount: number;
  onOpenNotifications: () => void;
  onCloseNotifications: () => void;
  menuOpen: boolean;
  menuId: string;
  onOpenMenu: () => void;
}

const formatCount = (n: number): string => (n > 99 ? '99+' : String(n));

/** `ResizeObserver` — в lib TypeScript проекта его ещё нет (как у EmailHtmlFrame). */
interface IResizeObserverLike {
  observe: (target: Element) => void;
  disconnect: () => void;
}

type TResizeObserverCtor = new (callback: () => void) => IResizeObserverLike;

const resizeObserverCtor = (): TResizeObserverCtor | undefined =>
  ((window as unknown) as { ResizeObserver?: TResizeObserverCtor })
    .ResizeObserver;

/** Ступени шрифта подписи: 11 → 10 → 9,5 px, пока слово не влезет. */
const LABEL_SIZES = [11, 10, 9.5];

/**
 * Подпись вкладки, которая не обрезается. Пятая часть 390 px — около 74 px:
 * длинное слово («Успеваемость», «Powiadomienia») шрифт уменьшает по
 * ступеням, подпись из нескольких слов («Таск-трекер») переносится на
 * вторую строку. Меряет сама себя после отрисовки, загрузки шрифта и
 * любой смены своей ширины (поворот экрана, дорисованная вкладка).
 */
const FitLabel = ({ children }: { children: string }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [step, setStep] = useState(0);
  // Повод перемерить, даже если ступень уже 0 (setStep(0) не перерисует).
  const [pass, setPass] = useState(0);
  const multiWord = /[\s-]/.test(children.trim());

  useLayoutEffect(() => {
    let alive = true;
    const remeasure = () => {
      if (!alive) return;
      setStep(0);
      setPass((value) => value + 1);
    };
    remeasure();
    window.addEventListener('resize', remeasure);
    // Шрифт ERP подгружается позже первой отрисовки и шире запасного: без
    // повторного замера «Коммуникатор» обрезался на 390. `ready` уже может
    // быть выполнен до начала загрузки — поэтому ещё и `loadingdone`.
    const { fonts } = document as Document & {
      fonts?: EventTarget & { ready: Promise<unknown> };
    };
    fonts?.ready.then(remeasure).catch(() => undefined);
    fonts?.addEventListener?.('loadingdone', remeasure);
    // Ширина вкладки меняется и без resize: набор вкладок дорисовывается,
    // когда приходят флаги роли (договоры появились — вкладки стали уже).
    let lastWidth = ref.current?.clientWidth ?? 0;
    const Observer = resizeObserverCtor();
    const observer =
      Observer && ref.current
        ? new Observer(() => {
            const width = ref.current?.clientWidth ?? 0;
            if (width === lastWidth) return;
            lastWidth = width;
            remeasure();
          })
        : null;
    if (observer && ref.current) observer.observe(ref.current);
    return () => {
      alive = false;
      window.removeEventListener('resize', remeasure);
      fonts?.removeEventListener?.('loadingdone', remeasure);
      observer?.disconnect();
    };
  }, [children]);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node || step >= LABEL_SIZES.length - 1) return;
    const tooWide = node.scrollWidth > node.clientWidth + 0.5;
    const tooTall = node.scrollHeight > node.clientHeight + 0.5;
    if (tooWide || tooTall) setStep(step + 1);
  }, [step, pass, children]);

  return (
    <Label ref={ref} $size={LABEL_SIZES[step]} $multi={multiWord}>
      {children}
    </Label>
  );
};

const withCount = (label: string, count: number): string | undefined =>
  count ? `${label} (${count})` : undefined;

const CountBadge = ({ count }: { count: number }): React.ReactElement | null =>
  count ? <Count aria-hidden="true">{formatCount(count)}</Count> : null;

/**
 * ТЗ 0121 — нижняя панель вкладок на телефоне: 3–4 раздела роли + «Ещё»
 * (лист меню). Какие вкладки — решает `getMobileTabs` (по видимому меню),
 * здесь только вёрстка.
 */
export const MobileTabBar = ({
  tabs,
  pathname,
  countOf,
  notificationsOpen,
  notificationsCount,
  onOpenNotifications,
  onCloseNotifications,
  menuOpen,
  menuId,
  onOpenMenu,
}: IMobileTabBar) => {
  const { t } = useTranslation();
  /*
   * Повторное нажатие на «Уведомления» закрывает панель. Поповер antd
   * закрывается сам по нажатию «мимо» (mousedown/touchstart на document) —
   * раньше, чем до вкладки дойдёт click, и click открыл бы панель снова.
   * Поэтому состояние запоминаем на pointerdown, до antd: была открыта —
   * значит это нажатие её закрывает. С клавиатуры pointerdown нет — тогда
   * решает текущее состояние.
   */
  const openAtPress = useRef<boolean | null>(null);
  const onNotificationsClick = () => {
    const wasOpen = openAtPress.current ?? notificationsOpen;
    openAtPress.current = null;
    if (wasOpen) onCloseNotifications();
    else onOpenNotifications();
  };

  const onTabPage = tabs.some(
    (tab) => tab.to !== null && isPathActive(pathname, tab.match ?? tab.to),
  );

  return (
    <Bar aria-label={t('mobile.tabs.label')} data-testid="mobile-tab-bar">
      {tabs.map((tab) => {
        const label = tab.labelKey ? t(tab.labelKey) : tab.text;
        if (!tab.to) {
          return (
            <TabButton
              key={tab.key}
              type="button"
              $active={notificationsOpen}
              aria-pressed={notificationsOpen}
              aria-label={withCount(label, notificationsCount)}
              onPointerDown={() => {
                openAtPress.current = notificationsOpen;
              }}
              onClick={onNotificationsClick}
            >
              <IconWrap>
                <Bell size={22} strokeWidth={2} aria-hidden="true" />
                <CountBadge count={notificationsCount} />
              </IconWrap>
              <FitLabel>{label}</FitLabel>
            </TabButton>
          );
        }
        const active = isPathActive(pathname, tab.match ?? tab.to);
        const count = countOf(tab.to);
        const TabIcon = tab.icon;
        return (
          <TabLink
            key={tab.key}
            to={tab.to}
            $active={active}
            aria-current={active ? 'page' : undefined}
            aria-label={withCount(label, count)}
          >
            <IconWrap>
              {TabIcon ? (
                <TabIcon size={22} strokeWidth={2} aria-hidden="true" />
              ) : null}
              <CountBadge count={count} />
            </IconWrap>
            <FitLabel>{label}</FitLabel>
          </TabLink>
        );
      })}
      <TabButton
        type="button"
        // Страница не на панели (почта, контакты, …) — подсвечено «Ещё»:
        // видно, где ты; сам пункт подсвечен в листе меню.
        $active={menuOpen || !onTabPage}
        aria-haspopup="dialog"
        aria-expanded={menuOpen}
        aria-controls={menuId}
        onClick={onOpenMenu}
      >
        <IconWrap>
          <Ellipsis size={22} strokeWidth={2} aria-hidden="true" />
        </IconWrap>
        <FitLabel>{t('mobile.tabs.more')}</FitLabel>
      </TabButton>
    </Bar>
  );
};

const Bar = styled.nav`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${LAYERS.HEADER};
  display: flex;
  align-items: stretch;
  box-sizing: content-box;
  height: ${MOBILE_TABBAR_HEIGHT}px;
  padding: 0 env(safe-area-inset-right) env(safe-area-inset-bottom)
    env(safe-area-inset-left);
  border-top: 1px solid ${T.border};
  background: ${T.surface};
`;

const tabStyle = css<{ $active: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 4px 1px 2px;
  border: none;
  background: none;
  font: inherit;
  text-decoration: none;
  cursor: pointer;
  color: ${({ $active }) => ($active ? T.accentText : T.textMuted)};
  -webkit-tap-highlight-color: transparent;

  &:hover,
  &:focus {
    color: ${({ $active }) => ($active ? T.accentText : T.textMuted)};
  }

  &:focus-visible {
    outline: 2px solid ${T.focusRing};
    outline-offset: -2px;
  }
`;

const TabLink = styled(Link)<{ $active: boolean }>`
  ${tabStyle}
`;

const TabButton = styled.button<{ $active: boolean }>`
  ${tabStyle}
`;

/** Значок вкладки; счётчик висит на нём и в ширину подписи не входит. */
const IconWrap = styled.span`
  position: relative;
  flex: none;
  display: inline-flex;
`;

const Count = styled.span`
  position: absolute;
  top: -5px;
  left: 14px;
  white-space: nowrap;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  box-sizing: border-box;
  border-radius: 999px;
  background: ${T.accent};
  color: ${T.onAccent};
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  font-variant-numeric: tabular-nums;
`;

const Label = styled.span<{ $size: number; $multi: boolean }>`
  display: block;
  box-sizing: border-box;
  width: 100%;
  overflow: hidden;
  font-size: ${({ $size }) => $size}px;
  font-weight: 500;
  /* Одно слово — одна строка; несколько слов — не больше двух строк. */
  line-height: ${({ $multi }) => ($multi ? '12px' : '14px')};
  max-height: ${({ $multi }) => ($multi ? '24px' : '14px')};
  letter-spacing: -0.1px;
  text-align: center;
  white-space: ${({ $multi }) => ($multi ? 'normal' : 'nowrap')};
  /* Крайний случай (ещё длиннее 9,5 px) — многоточие, а не обрезанная буква. */
  text-overflow: ellipsis;
  overflow-wrap: normal;
  hyphens: none;
`;
